/**
 * Navigation Utilities
 * 
 * Helpers for tab navigation and routing within the plugin manager
 */

import { NavigateFunction } from 'react-router-dom';

export type TabName = 'overview' | 'installed' | 'marketplace' | 'create' | 'drafts' | 'security';

// Global navigate function - will be set by plugin manager
let globalNavigate: NavigateFunction | null = null;

export function setGlobalNavigate(navigate: NavigateFunction) {
  globalNavigate = navigate;
}

/**
 * Tab configuration
 */
export interface TabConfig {
  value: TabName;
  label: string;
  icon: string;
  path: string;
  description: string;
  keyboardShortcut: string;
}

/**
 * All available tabs
 */
export const TABS: TabConfig[] = [
  {
    value: 'overview',
    label: 'Overview',
    icon: '📊',
    path: '/app-center',
    description: 'App Center overview and quick actions',
    keyboardShortcut: 'Alt+1'
  },
  {
    value: 'installed',
    label: 'My Apps',
    icon: '📦',
    path: '/app-center/installed',
    description: 'Manage your apps',
    keyboardShortcut: 'Alt+2'
  },
  {
    value: 'marketplace',
    label: 'Apps Library',
    icon: '🏪',
    path: '/app-center/marketplace',
    description: 'Browse and build new apps',
    keyboardShortcut: 'Alt+3'
  },
  {
    value: 'create',
    label: 'Create New',
    icon: '✨',
    path: '/app-center/create',
    description: 'Choose how to create your app',
    keyboardShortcut: 'Alt+4'
  },
  {
    value: 'drafts',
    label: 'Drafts',
    icon: '📝',
    path: '/app-center/drafts',
    description: 'Manage draft apps',
    keyboardShortcut: 'Alt+5'
  },
  {
    value: 'security',
    label: 'Security Review',
    icon: '🔒',
    path: '/app-center/security',
    description: 'Plugin security governance and review',
    keyboardShortcut: 'Alt+6'
  }
];

/**
 * Get tab config by value
 */
export function getTabConfig(tab: TabName): TabConfig | undefined {
  return TABS.find(t => t.value === tab);
}

/**
 * Get tab from URL path
 */
export function getTabFromPath(path: string): TabName {
  const segments = path.split('/').filter(Boolean);
  
  if (segments.length === 1 && segments[0] === 'app-center') {
    return 'overview';
  }
  
  if (segments.length === 2 && segments[0] === 'app-center') {
    const tabName = segments[1] as TabName;
    if (TABS.some(t => t.value === tabName)) {
      return tabName;
    }
  }
  
  return 'overview';
}

/**
 * Navigate to tab (updates URL using React Router if available, otherwise fallback to pushState)
 */
export function navigateToTab(tab: TabName): void {
  const config = getTabConfig(tab);
  if (!config) return;
  
  // Prefer React Router navigation if available
  if (globalNavigate) {
    globalNavigate(config.path);
    // Dispatch custom event for listeners
    window.dispatchEvent(
      new CustomEvent('tab-navigate', {
        detail: { tab, path: config.path }
      })
    );
    return;
  }
  
  // Fallback to pushState (for backwards compatibility)
  if (typeof window !== 'undefined' && window.history) {
    window.history.pushState(null, '', config.path);
    
    // Dispatch custom event for listeners
    window.dispatchEvent(
      new CustomEvent('tab-navigate', {
        detail: { tab, path: config.path }
      })
    );
  }
}

/**
 * Replace current URL without adding to history
 */
export function replaceTab(tab: TabName): void {
  const config = getTabConfig(tab);
  if (!config) return;
  
  if (typeof window !== 'undefined' && window.history) {
    window.history.replaceState(null, '', config.path);
  }
}

/**
 * Get current tab from browser location
 */
export function getCurrentTab(): TabName {
  if (typeof window === 'undefined') return 'overview';
  return getTabFromPath(window.location.pathname);
}

